// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadAccountShipToCollection from "@insite/client-framework/Store/Data/AccountShipTos/Handlers/LoadAccountShipToCollection";
import saveShipToCollection from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SaveShipToCollection";
import updateSearchFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import { AccountShipToModel, CustomerCostCodeDto, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableBody, Select } from "@insite/mobius/index";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import myAccountFilterSectionStyles from "@werner/App/SharedStyle/MyAccount/List/MyAccountFilterSectionStyles";
import { xngGetAccountShipTosDataView } from "@werner/App/Store/Selectors/AccountShipToSelectors";
// XNG-Changes: import twobuttonmodal from override
import Modal, { ModalPresentationProps } from "@werner/MobiusOverrides/Modal/Modal";
import Tooltip, { TooltipPresentationProps } from "@werner/MobiusOverrides/Tooltip/Tooltip";
import { modalButtonsActionStyles, pluralize, styledScrollbar, xScreen } from "@werner/XDL/Utilities";
import cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isModalOpen: boolean;
    closeModalHandler: () => void;
    extendedStyles?: AssignShipToAddressModalStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const accountShipTosDataView = xngGetAccountShipTosDataView(
        state,
        state.pages.userSetup.getAccountShipToCollectionParameter,
    );

    return {
        // XNG-Changes: load dataview from a custom selector
        accountShipTosDataView,
        parameter: state.pages.userSetup.getAccountShipToCollectionParameter,
    };
};

const mapDispatchToProps = {
    loadAccountShipToCollection,
    updateSearchFields,
    saveShipToCollection,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AssignShipToAddressModalStyles {
    modal?: ModalPresentationProps;
    shipToContainer?: GridContainerProps;
    shipToNumberGridItem?: GridItemProps;
    shipToNumberLabel?: TypographyPresentationProps;
    shipToNumberText?: TypographyPresentationProps;
    addressInfoGridItem?: GridItemProps;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    assignShippingAddressGridItem?: GridItemProps;
    assignShippingAddressCheckbox?: CheckboxPresentationProps;
    pagination?: PaginationPresentationProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    // XNG-Changes
    container?: InjectableCss;
    itemsCountText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableHeader?: DataTableHeaderProps;
    dataTableRow?: DataTableRowProps;
    dataTableCell?: DataTableCellProps;
    dataTableCellText?: TypographyPresentationProps;
    shipToNickNameText?: TypographyPresentationProps;
    shipToAssignContainer?: InjectableCss;
    costCodesSelect?: SelectPresentationProps;
    tooltipStyles?: TooltipPresentationProps;
}

export const assignShipToAddressModalStyles: AssignShipToAddressModalStyles = {
    modal: {
        sizeVariant: "large",
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalActions: css`
                ${xScreen("lte", "sm")} {
                    padding: 20px;
                }
            `,
        },
    },
    container: {
        css: css`
            overflow: auto;
            padding: 16px 16px 16px 30px;
            ${styledScrollbar()}
        `,
    },
    dataTableCellText: {
        size: 15,
        lineHeight: "22px",
        weight: 400,
    },
    shipToNickNameText: {
        size: 15,
        lineHeight: "22px",
        weight: 400,
        css: css`
            max-width: 180px;
            width: 100%;
            text-overflow: ellipsis;
            overflow: hidden;
            display: inline-block;
        `,
    },
    itemsCountText: {
        ...myAccountFilterSectionStyles.resultCountTextStyles,
    },
    shipToAssignContainer: {
        css: css`
            display: flex;
            gap: 12px;
            align-items: center;
        `,
    },
    cancelButton: {
        ...modalButtonsActionStyles.cancelButton,
    },
    saveButton: {
        ...modalButtonsActionStyles.submitButton,
    },
    costCodesSelect: {
        cssOverrides: {
            formField: css`
                max-width: 200px;
                width: 100%;
                min-width: 200px;
            `,
        },
    },
    tooltipStyles: {
        mergeCss: true,
        cssOverrides: {
            tooltipBody: css`
                > ${TypographyStyle} {
                    text-wrap: wrap;
                }
            `,
        },
    },
};

const AssignShipToAddressModal = ({
    isModalOpen,
    accountShipTosDataView,
    parameter,
    closeModalHandler,
    extendedStyles,
    loadAccountShipToCollection,
    updateSearchFields,
    saveShipToCollection,
}: Props) => {
    useEffect(() => {
        if (!accountShipTosDataView.value && !accountShipTosDataView.isLoading) {
            loadAccountShipToCollection(parameter);
        }
    });

    useEffect(() => {
        if (accountShipTosDataView.value) {
            setShipToCollection(accountShipTosDataView.value);
            setPagination(accountShipTosDataView.pagination);
        }
    }, [accountShipTosDataView.value]);

    useEffect(() => {
        const nickNames = accountShipTosDataView.properties
            ? JSON.parse(accountShipTosDataView.properties?.shipTosNickNames || "[]")
            : [];
        setShipTosNickNames(nickNames);
    }, [accountShipTosDataView.properties]);

    useEffect(() => {
        setCostCodesCollection(accountShipTosDataView.costCodesCollection);
    }, [accountShipTosDataView.costCodesCollection]);

    const [shipToCollection, setShipToCollection] = useState<AccountShipToModel[] | undefined>();

    const [costCodesCollection, setCostCodesCollection] = React.useState<CustomerCostCodeDto[] | null>();

    const [shipTosNickNames, setShipTosNickNames] = React.useState<
        [{ customerSequence: string; nickName: string }] | undefined
    >();
    const [pagination, setPagination] = useState<PaginationModel | null | undefined>();
    const [isSaving, setIsSaving] = useState(false);
    const [styles] = useState(() => mergeToNew(assignShipToAddressModalStyles, extendedStyles));

    const totalItems = accountShipTosDataView.pagination ? accountShipTosDataView.pagination.totalItemCount : 0;

    const assignShippingAddressChangeHandler = (value: boolean, index: number) => {
        if (!shipToCollection) {
            return;
        }
        const currentShipToCollection = cloneDeep(shipToCollection);
        currentShipToCollection[index].assign = value;
        setShipToCollection(currentShipToCollection);

        setIsSaving(true);
        saveShipToCollection({
            shipToCollection: currentShipToCollection,
            onSuccess: () => {
                setIsSaving(false);
            },
            onError: () => {
                setIsSaving(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const setCostCodeChangeHandler = (value: string, index: number) => {
        if (shipToCollection) {
            const currentShipToCollection = cloneDeep(shipToCollection);
            currentShipToCollection[index].costCode = value;
            setShipToCollection(currentShipToCollection);
        }
    };

    const handleChangePage = (page: number) => {
        updateSearchFields({ page });
    };

    const handleChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = Number(event.target.value);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };

    const saveButtonClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (!shipToCollection) {
            return;
        }

        closeModalHandler();
    };

    // XNG-Changes
    const assignShipToAddressModalActions = (
        <>
            <Button
                {...styles.saveButton}
                onClick={e => saveButtonClickHandler(e)}
                data-test-selector="assignShipToAddressModal_saveButton"
            >
                {translate("Done")}
            </Button>
        </>
    );
    // XNG-Changes: send actions buttons as a props and delete it from modal content
    return (
        <Modal
            {...styles.modal}
            headline={translate("Assign / Edit Ship To")}
            isOpen={isModalOpen}
            handleClose={closeModalHandler}
            data-test-selector="assignShiptoAddressModal"
            actions={assignShipToAddressModalActions}
        >
            <StyledWrapper {...styles.container} data-test-selector="userSetupShipTosTable">
                <Typography {...styles.itemsCountText}>
                    {`${totalItems} ${translate(pluralize(totalItems, "Record"))}`}
                </Typography>
                <DataTable {...styles.dataTable}>
                    <DataTableHead {...styles.dataTableHead}>
                        <DataTableHeader
                            {...styles.dataTableHeader}
                            // sorted={sorted("UserName")}
                            // onSortClick={() => headerClick("UserName")}
                            data-test-selector="userSetupShipTosTable_shipTo"
                        >
                            {translate("Ship To #")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.dataTableHeader}
                            // sorted={sorted("UserName")}
                            // onSortClick={() => headerClick("UserName")}
                            data-test-selector="userSetupShipTosTable_shipTo"
                        >
                            {translate("City")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.dataTableHeader}
                            // sorted={sorted("UserName")}
                            // onSortClick={() => headerClick("UserName")}
                            data-test-selector="userSetupShipTosTable_shipTo"
                        >
                            {translate("State")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.dataTableHeader}
                            // sorted={sorted("UserName")}
                            // onSortClick={() => headerClick("UserName")}
                            data-test-selector="userSetupShipTosTable_shipTo"
                        >
                            {translate("Street Address")}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody {...styles.dataTableBody}>
                        {shipToCollection?.map((shipTo, index) => {
                            const shipTosNickName = shipTosNickNames?.[index];
                            const nickName = shipTosNickName
                                ? `${shipTosNickName.nickName} #${shipTosNickName.customerSequence}`
                                : "";
                            return (
                                <DataTableRow
                                    {...styles.dataTableRow}
                                    key={shipTo.shipToNumber}
                                    data-test-selector="userSetupShipTosTable_row"
                                    data-test-key={shipTo.id}
                                >
                                    <DataTableCell
                                        {...styles.dataTableCell}
                                        as="th"
                                        scope="row"
                                        data-test-selector="userSetupShipTosTable_cell_assignShipTo"
                                        data-test-key={shipTo.id}
                                    >
                                        <StyledWrapper {...styles.shipToAssignContainer}>
                                            <Checkbox
                                                {...styles.assignShippingAddressCheckbox}
                                                checked={shipTo.assign}
                                                onChange={(e, value) =>
                                                    assignShippingAddressChangeHandler(value, index)
                                                }
                                                disabled={
                                                    shipTo.shipToNumber.length === 0 ||
                                                    isSaving ||
                                                    accountShipTosDataView.isLoading
                                                }
                                                data-test-selector="assignShipToAddressModal_assign"
                                                data-test-key={shipTo.shipToNumber}
                                            >
                                                {translate("Assign")}
                                            </Checkbox>
                                            <Select
                                                {...styles.costCodesSelect}
                                                label={translate("Cost Codes")}
                                                value={shipTo.costCode}
                                                onChange={event => setCostCodeChangeHandler(event.target.value, index)}
                                            >
                                                <option value="">{translate("Select")}</option>
                                                {costCodesCollection &&
                                                    costCodesCollection?.map(cc => (
                                                        <option key={cc.customerCostCodeId} value={cc.costCode}>
                                                            {cc.costCode}
                                                        </option>
                                                    ))}
                                            </Select>
                                            <Tooltip
                                                text={nickName}
                                                {...styles.tooltipStyles}
                                                triggerComponent={
                                                    <Typography {...styles.shipToNickNameText}>{nickName}</Typography>
                                                }
                                            />
                                        </StyledWrapper>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataTableCell}
                                        as="th"
                                        scope="row"
                                        data-test-selector="userSetupShipTosTable_cell_city"
                                        data-test-key={shipTo.id}
                                    >
                                        <Typography {...styles.dataTableCellText}>{shipTo.city}</Typography>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataTableCell}
                                        as="th"
                                        scope="row"
                                        data-test-selector="userSetupShipTosTable_cell_state"
                                        data-test-key={shipTo.id}
                                    >
                                        <Typography {...styles.dataTableCellText}>{shipTo.state}</Typography>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataTableCell}
                                        as="th"
                                        scope="row"
                                        data-test-selector="userSetupShipTosTable_cell_streetAddress"
                                        data-test-key={shipTo.id}
                                    >
                                        <Typography {...styles.dataTableCellText}>{shipTo.address}</Typography>
                                    </DataTableCell>
                                </DataTableRow>
                            );
                        })}
                    </DataTableBody>
                </DataTable>
                {pagination && pagination.totalItemCount > 1 && (
                    <Pagination
                        {...styles.pagination}
                        resultsCount={pagination.totalItemCount}
                        currentPage={pagination.page}
                        resultsPerPage={pagination.pageSize}
                        resultsPerPageOptions={pagination.pageSizeOptions}
                        onChangePage={handleChangePage}
                        onChangeResultsPerPage={handleChangeResultsPerPage}
                        prevPage={pagination.prevPageUri}
                        nextPage={pagination.nextPageUri}
                    />
                )}
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AssignShipToAddressModal);
